import { ConfigProvider } from "antd";
import { CSSProperties, PropsWithChildren, ReactElement, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { getClassNameForComponent } from "../../utils/stringUtils";
import { excludeProps } from "../../utils/styledUtils";
import { getAppBarHeight } from "./AppLayout";

export interface MaskProps {
  visible: boolean;
  onDismiss(): void;
  horizontalAlign?: CSSProperties["justifyContent"];
  verticalAlign?: CSSProperties["alignItems"];
}

export default function Mask({ children, visible, onDismiss, horizontalAlign }: PropsWithChildren<MaskProps>): ReactElement {
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(
    () => {
      setIsTransitioning(true);
    },
    [visible],
  );

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const popupContainerRef = useRef<HTMLDivElement>(undefined!);

  return (
    <MaskDiv
      visible={visible}
      isTransitioning={isTransitioning}
      onTransitionEnd={() => setIsTransitioning(false)}
      onClick={(e) => { if (e.currentTarget === e.target) onDismiss(); }}
      horizontalAlign={horizontalAlign}
      className={getClassNameForComponent({ Mask })}
    >
      <PopupContainerDiv ref={popupContainerRef} className={getClassNameForComponent({ Mask, PopupContainerDiv: true })} />
      <ConfigProvider getPopupContainer={() => popupContainerRef.current}>
        {children}
      </ConfigProvider>
    </MaskDiv>
  );
}

export const maskZIndex = 10000;

const PopupContainerDiv = styled.div`
  position: absolute;
`;

const MaskDiv = styled.div.withConfig({ shouldForwardProp: excludeProps(["visible", "isTransitioning", "horizontalAlign", "verticalAlign"]) }) <{ visible: boolean; isTransitioning: boolean; horizontalAlign?: CSSProperties["alignItems"]; verticalAlign?: CSSProperties["alignItems"] }>`
  z-index: ${maskZIndex};
  position: absolute;
  top: ${p => getAppBarHeight(p.theme)}px;
  bottom: 0;
  left: 0;
  right: 0;

  display: ${p => p.visible || p.isTransitioning ? "flex" : "none"};
  justify-content: ${p => p.horizontalAlign};
  align-items: ${p => p.verticalAlign};

  color: ${p => p.theme.colorText};
  background-color: ${p => p.theme.colorBgMask};

  @keyframes aui-Mask-appear {
    from {
      opacity: 0;
      pointer-events: none;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes aui-Mask-disappear {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
      pointer-events: none;
    }
  }

  animation: ${p => p.visible ? "aui-Mask-appear" : "aui-Mask-disappear"} ${p => p.theme.motionDurationMid} ${p => p.theme.motionEaseInOut};
  animation-fill-mode: both;
`;
